import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { RangeValue } from '@react-types/shared';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import AnalyticsDatePicker from 'dashboard/components/AnalyticsDatePicker';
import { AnalyticsDateRange } from 'dashboard/components/AnalyticsDatePicker/AnalyticsDatePicker';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import LoadingOverlay from 'dashboard/components/LoadingOverlay';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { MetricsTransactionVolumeOverTime } from 'dashboard/models/metrics-transaction';
import { TransactionType } from 'dashboard/models/transaction-type';
import { MetricsFilterParams } from 'dashboard/redux/actions/metrics-transaction';
import { initialState } from 'dashboard/redux/reducers/metrics-transaction';
import TransactionMetrics from 'dashboard/views/DashboardTransactionAnalyticsView/TransactionMetrics';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { Label } from 'design-system/components/Text';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';
import { Iso8601DateString } from 'lib/types';

const SearchAndFilterCardBody = styled(Card.Body)``;

const SearchAndFilterCardTitle = styled(Card.Title)``;

const SearchAndFilterContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacing[3]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    flex-direction: column;
    gap: 0;
  }
`;

const TemplatesDropdownContainer = styled.div`
  width: 200px;
  flex: 1 1 auto;

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    width: 100%;
  }
`;

const DatePickerContainer = styled.div`
  min-width: 290px;
`;

const RunContainer = styled.div`
  margin-left: ${Spacing[4]};
  min-width: 100px;
  display: grid;
  grid-template-rows: 24px 1fr;
  justify-items: right;
  padding-top: 6px;
`;

const RunButton = styled(Button)`
  height: ${Spacing[6]};
  grid-row-start: 2;
`;

interface DashboardTransactionAnalyticsViewProps {
  filters: MetricsFilterParams;
  handleTransactionTypeFilterUpdate: (transactionTypeIds: string[]) => void;
  isLoading: boolean;
  loadTransactionMetrics: (transactionTypeIds: string[]) => Promise<void>;
  isLoadingTransactionTypesList: boolean;
  volumeOverTime: MetricsTransactionVolumeOverTime;
  queriedAt?: Iso8601DateString;
  transactionTypes: TransactionType[];
  selectedTransactionTypes: TransactionType[];
  handleDateUpdateV2: (range: RangeValue<DateValue> | null) => void;
}

const DashboardTransactionAnalyticsView = ({
  filters,
  handleDateUpdateV2,
  handleTransactionTypeFilterUpdate,
  isLoading,
  loadTransactionMetrics,
  isLoadingTransactionTypesList,
  volumeOverTime,
  queriedAt,
  transactionTypes,
  selectedTransactionTypes,
}: DashboardTransactionAnalyticsViewProps) => {
  const { createdAtStart, createdAtEnd } = filters;
  const dateRange = useMemo<AnalyticsDateRange>(() => {
    return {
      startDate: createdAtStart ? DateTime.fromISO(createdAtStart, { setZone: true }) : undefined,
      endDate: createdAtEnd ? DateTime.fromISO(createdAtEnd, { setZone: true }) : undefined,
    };
  }, [createdAtStart, createdAtEnd]);

  const transactionTypeFilterOptions = useMemo(
    () =>
      transactionTypes.map((transactionType) => ({
        label: transactionType.attributes.name,
        value: transactionType.id,
      })),
    [transactionTypes],
  );

  const transactionTypeFilterLabelTextMapper = useLabelTextMapper(
    transactionTypeFilterOptions,
    'transaction types',
    {
      emptyIsAll: true,
    },
  );

  const loaded = volumeOverTime !== initialState.volumeOverTime;
  const hasTransactionViewPermission = usePermission(Permission.TransactionView);

  return (
    <Container>
      <InactiveEnvironmentBanner />
      <Header title='Analytics: Transaction' />
      <Section>
        <Card>
          {isLoading && <LoadingOverlay />}
          <SearchAndFilterCardBody>
            <SearchAndFilterCardTitle>Query</SearchAndFilterCardTitle>
            <SearchAndFilterContainer>
              <DatePickerContainer>
                <AnalyticsDatePicker
                  dataTest='metrics-transactions-view__date-range'
                  handleDatesChangeV2={handleDateUpdateV2}
                  dateRange={dateRange}
                  disabled={isLoading}
                />
              </DatePickerContainer>
              <TemplatesDropdownContainer>
                <FormControlLabel>Transaction Types</FormControlLabel>
                <MultiSelect
                  value={selectedTransactionTypes.map((transactionType) => transactionType.id)}
                  selectOptionValues={transactionTypeFilterOptions}
                  labelTextMapper={transactionTypeFilterLabelTextMapper}
                  onChange={handleTransactionTypeFilterUpdate}
                  data-test='metrics-transactions-view__select-template'
                  disabled={!transactionTypes.length}
                  size='md'
                  selectAllEnabled
                  searchEnabled
                />
              </TemplatesDropdownContainer>
              <RunContainer>
                {queriedAt != null && (
                  <Label size='md' color='tertiary' weight='bold'>
                    Last ran {DateTime.fromISO(queriedAt).toRelative()}
                  </Label>
                )}
                <RunButton
                  id='metrics-transactions-view__run'
                  data-test='metrics-transactions-view__run'
                  onClick={() =>
                    loadTransactionMetrics(
                      selectedTransactionTypes.map((transactionType) => transactionType.id),
                    )
                  }
                  isPending={isLoading || isLoadingTransactionTypesList}
                  disabled={!hasTransactionViewPermission}
                  width='auto'
                  size='sm'
                >
                  Run
                </RunButton>
              </RunContainer>
            </SearchAndFilterContainer>
          </SearchAndFilterCardBody>
        </Card>
        {loaded && (
          <TransactionMetrics
            isLoading={isLoading}
            volumeOverTime={volumeOverTime}
            period={volumeOverTime.period}
          />
        )}
      </Section>
    </Container>
  );
};

export default DashboardTransactionAnalyticsView;
